/**
 * Pure derivation logic for the "Edge vs a same-size market basket" block (lane
 * OBS-REL), split from `MarketRelativeBlock.ts` so every rule is unit-testable
 * without the DOM. Reads only `refs.benchmarks.market_relative`
 * (`organism.benchmark.relative.build_market_relative`): nothing here computes a
 * statistic the publisher did not — it labels, orders and scales what was
 * published and degrades every missing value to "–" (never a guessed 0).
 *
 * Why the block exists: both flies run core-only with the tilt off, so the dollar
 * chart tracks the market almost exactly. Subtracting a basket of the same size
 * leaves only what the fly did differently — its edge, or its drag.
 */

import type {
  BenchmarksDocument,
  MarketRelativeComponent,
  MarketRelativeDocument,
} from "../types/incremental";
import type { ChartPoint } from "./benchmarksView";
import { formatPercent, formatSignedPercent, type ToneText } from "../format/number";

export type ExcessSeriesName = "excess_vs_basket" | "excess_vs_tbills";

export const EXCESS_SERIES_ORDER: readonly ExcessSeriesName[] = [
  "excess_vs_basket",
  "excess_vs_tbills",
];

export const EXCESS_SERIES_LABELS: Readonly<Record<ExcessSeriesName, string>> = {
  excess_vs_basket: "Edge vs a same-size market basket",
  excess_vs_tbills: "Excess over T-bills (left axis)",
};

export const EXCESS_SERIES_TOOLTIPS: Readonly<Record<ExcessSeriesName, string>> = {
  excess_vs_basket:
    "The fly's return minus an equal-weight basket of its own universe scaled each day to the " +
    "fly's actual held core gross (the rest in T-bills, same opening capital and tariff). Flat at " +
    "0 = no edge; rising = edge; falling = drag (costs, timing, idle cash).",
  excess_vs_tbills:
    "The fly's return minus T-bills (FRED DTB3) compounded since genesis — what holding risk has " +
    "paid over cash. Mostly the market's move while the fly tracks its basket.",
};

/** Same validated slots as the Benchmarks chart: live blue for the edge line,
 * the neutral cash grey (dashed) for the T-bill line. */
export const EXCESS_SERIES_COLORS: Readonly<Record<ExcessSeriesName, string>> = {
  excess_vs_basket: "#3987e5",
  excess_vs_tbills: "#5c667a",
};

export const EXCESS_SERIES_DASHED: ReadonlySet<ExcessSeriesName> = new Set(["excess_vs_tbills"]);

export type MarketRelativeState =
  | { readonly kind: "empty"; readonly message: string; readonly tiltActive: boolean | null }
  | { readonly kind: "ready"; readonly block: MarketRelativeDocument };

export function buildMarketRelativeState(doc: BenchmarksDocument | null): MarketRelativeState {
  const block = doc?.market_relative;
  if (!block) {
    return {
      kind: "empty",
      message:
        "Not published yet — the market-relative view appears with the first publish after " +
        "the publisher is updated (it rebuilds the benchmark fold from genesis once).",
      tiltActive: null,
    };
  }
  if (block.status !== "ok" || !block.headline || !block.series || !block.decomposition) {
    return {
      kind: "empty",
      message:
        `Not enough data yet: ${block.days} of ${block.min_days} counted days. A fly's edge ` +
        "needs at least two daily returns to compare against the basket.",
      tiltActive: block.tilt_active,
    };
  }
  return { kind: "ready", block };
}

/** The tilt badge: "the tilt is OFF" whenever no non-zero tilt was ever recorded
 * (the live `ORGANISM_TILT_RANGE=0` drop-in: every recorded tilt is exactly 0). */
export function tiltBadge(active: boolean | null): {
  readonly text: string;
  readonly className: string;
  readonly tooltip: string;
} {
  if (active === true) {
    return {
      text: "the tilt is ON",
      className: "obs-rel-tilt-on",
      tooltip:
        "A non-zero tilt has been recorded: the brain is moving the book away from the core. " +
        "The tilt line below is its contribution (live minus the core-only reference).",
    };
  }
  return {
    text: "the tilt is OFF",
    className: "obs-rel-tilt-off",
    tooltip:
      "tilt_range = 0: every recorded tilt is exactly 0, so the fly holds the plain equal-weight " +
      "core. Its tilt contribution is 0 by construction — any edge or drag below is execution, " +
      "costs and idle cash, not the brain.",
  };
}

export interface HeadlineItem {
  readonly label: string;
  readonly value: ToneText;
  readonly tooltip: string;
}

/** "Tracking the market" as numbers: excess since genesis, the last 30 days,
 * and the tracking error (plus excess over T-bills, for scale). */
export function headlineItems(block: MarketRelativeDocument): HeadlineItem[] {
  const h = block.headline;
  const days = h?.window_counted_days ?? 0;
  return [
    {
      label: "Edge since genesis",
      value: formatSignedPercent(h?.excess_since_genesis),
      tooltip:
        "Fly return minus the same-size basket's since genesis. Positive = edge, negative = drag.",
    },
    {
      label: `Last ${h?.window_days ?? 30} days`,
      value: formatSignedPercent(h?.excess_30d),
      tooltip:
        `Same, compounded over the counted days since ${h?.window_start ?? "–"} ` +
        `(${days} day${days === 1 ? "" : "s"}).`,
    },
    {
      label: "Tracking error",
      value: { text: `${formatPercent(h?.tracking_error ?? null, 2)}/yr`, tone: "flat" },
      tooltip:
        "Annualized std of the daily difference between the fly and the basket. Near 0 = the fly " +
        "is the market at its own size; this is the number behind 'tracking the market'.",
    },
    {
      label: "vs T-bills since genesis",
      value: formatSignedPercent(h?.excess_vs_tbills_since_genesis),
      tooltip:
        "Fly return minus compounded T-bills — what taking market risk has paid so far (mostly " +
        "beta, not edge).",
    },
  ];
}

/** One plain sentence under the headline. */
export function trackingSentence(block: MarketRelativeDocument): string {
  const h = block.headline;
  const gross = block.gross?.latest;
  const size = gross === null || gross === undefined ? "its" : `a ${formatPercent(gross, 0)}`;
  const edge = formatSignedPercent(h?.excess_since_genesis).text;
  const te = formatPercent(h?.tracking_error ?? null, 2);
  return (
    `Against ${size} market basket of its own universe the fly is ${edge} since genesis, ` +
    `with a tracking error of ${te}/yr.`
  );
}

export interface DecompositionItem {
  readonly key: string;
  readonly label: string;
  readonly value: ToneText;
  readonly raw: number | null;
  /** Bar length as a share of the largest |component| (0..1). */
  readonly share: number;
  readonly tag: string | null;
  readonly note: string;
}

const COMPONENT_NOTES: Readonly<Record<string, string>> = {
  core: "The market move on the held equal-weight core (beta), before fees and spread — includes the exit-cost mark and execution timing.",
  tilt: "The brain's tilt: live minus the core-only reference once a non-zero tilt is recorded. Exactly 0 while the tilt is off.",
  rf: "The T-bill cash sleeve's P&L, net of its own fees. Risk-free return — rf, never edge. 0 while the sleeve is off.",
  costs: "Venue fees and spread paid by the core since genesis (sleeve fees are inside rf).",
  tariff: "The operating tariff — every benchmark pays it too.",
};

export function decompositionItems(block: MarketRelativeDocument): DecompositionItem[] {
  const components: readonly MarketRelativeComponent[] = block.decomposition?.components ?? [];
  const largest = Math.max(
    1e-12,
    ...components.map((c) => (c.value === null ? 0 : Math.abs(c.value))),
  );
  return components.map((c) => {
    const isTiltOff = c.key === "tilt" && !block.tilt_active;
    return {
      key: c.key,
      label: c.label,
      value: isTiltOff ? { text: "0 (tilt off)", tone: "flat" } : formatSignedPercent(c.value, 3),
      raw: c.value,
      share: c.value === null ? 0 : Math.abs(c.value) / largest,
      tag: c.tag ?? null,
      note: COMPONENT_NOTES[c.key] ?? c.label,
    };
  });
}

/** `= total` line; flags a published sum that drifts from the total (never expected). */
export function decompositionTotal(block: MarketRelativeDocument): {
  readonly text: string;
  readonly consistent: boolean;
} {
  const d = block.decomposition;
  const total = d?.total ?? null;
  const sum = d?.sum ?? null;
  const consistent = total !== null && sum !== null && Math.abs(total - sum) <= 1e-5;
  return { text: `= ${formatSignedPercent(total, 3).text} total return`, consistent };
}

/** One excess series as chart points (percent), strictly increasing whole seconds. */
export function excessPoints(block: MarketRelativeDocument, name: ExcessSeriesName): ChartPoint[] {
  const series = block.series;
  if (!series) return [];
  const values = series[name] ?? [];
  const out: ChartPoint[] = [];
  for (let i = 0; i < series.t.length; i += 1) {
    const value = values[i];
    const ms = Date.parse(series.t[i] ?? "");
    if (value === null || value === undefined || !Number.isFinite(value) || !Number.isFinite(ms)) {
      continue;
    }
    const time = Math.floor(ms / 1000);
    const point = { time, value: value * 100 };
    const last = out[out.length - 1];
    if (last && time <= last.time) {
      if (time === last.time) out[out.length - 1] = point;
      continue;
    }
    out.push(point);
  }
  return out;
}
